<script setup>
import {computed} from 'vue';
import {useRuntimeConfig} from "nuxt/app";

/**
 * ClubLivePreview — a live, on-brand preview of the club the user is creating.
 * Updates as they fill the wizard so setup feels tangible and exciting.
 */
const props = defineProps({
  item: {type: Object, required: true},
  firstName: {type: String, default: ''},
});

const {uploadFilenameToUrl} = useCrudConverters();
const mapsKey = useRuntimeConfig().public?.grantThomasNuxt?.googleMapsApiKey || useRuntimeConfig().public?.googleMapsApiKey;

const logoUrl = computed(() => {
  const f = props.item.logoUpload?.fileLocation || props.item.logoUpload?.filename;
  return f ? uploadFilenameToUrl(f) : null;
});

const staticMapUrl = computed(() => {
  const l = props.item.location;
  if (l?.latitude == null || !mapsKey) return null;
  return `https://maps.googleapis.com/maps/api/staticmap?center=${l.latitude},${l.longitude}`
      + `&zoom=14&size=400x150&scale=2`
      + `&markers=${encodeURIComponent(`color:0xD32946|${l.latitude},${l.longitude}`)}`
      + `&key=${mapsKey}`;
});

const initials = computed(() =>
    (props.item.name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase());

const locationLabel = computed(() => {
  if (!props.item.hasFixedAddress) return 'No fixed address';
  return props.item.city || 'Your venue';
});

const perks = [
  {icon: 'mdi-trophy-outline', text: 'Box leagues, ladders & tournaments'},
  {icon: 'mdi-account-multiple-plus-outline', text: 'Invite unlimited members'},
  {icon: 'mdi-rocket-launch-outline', text: 'Up and running in minutes'},
];
</script>

<template>
  <aside class="clp">
    <div class="clp-streaks" aria-hidden="true"/>
    <div class="clp-glow" aria-hidden="true"/>

    <div class="clp-inner">
      <div class="clp-eyebrow">Your club, taking shape</div>

      <div class="clp-card">
        <div class="clp-crest" :class="{ 'is-empty': !initials && !logoUrl }">
          <img v-if="logoUrl" :src="logoUrl" class="clp-crest-img" alt="Club logo"/>
          <span v-else-if="initials">{{ initials }}</span>
          <v-icon v-else size="26">mdi-shield-outline</v-icon>
        </div>
        <div class="clp-name" :class="{ 'is-placeholder': !item.name }">
          {{ item.name || 'Your club name' }}
        </div>
        <div class="clp-meta">
          <span class="clp-chip">
            <v-icon size="13" class="mr-1">mdi-map-marker</v-icon>{{ locationLabel }}
          </span>
          <span class="clp-chip clp-chip--ok">
            <v-icon size="13" class="mr-1">mdi-check-decagram</v-icon>Free
          </span>
        </div>
        <div class="clp-foot">
          <v-icon size="15" class="mr-1">mdi-account-circle</v-icon>
          1 member · {{ firstName || 'You' }} <span class="clp-foot-tag">admin</span>
        </div>

        <div v-if="staticMapUrl" class="clp-map">
          <img :src="staticMapUrl" alt="Club location" loading="lazy"/>
        </div>
      </div>

      <ul class="clp-perks">
        <li v-for="p in perks" :key="p.text">
          <v-icon size="16" class="clp-perk-ic">{{ p.icon }}</v-icon>{{ p.text }}
        </li>
      </ul>
    </div>
  </aside>
</template>

<style scoped lang="scss">
.clp {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 26px 22px;
  color: #fff;
  background: linear-gradient(150deg, #E0314F 0%, #D32946 45%, #A31C35 100%);
  box-shadow: 0 30px 60px -32px rgba(163, 28, 53, 0.7);
}

.clp-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(120deg, transparent 0 36px, rgba(255, 255, 255, 0.05) 36px 38px);
}

.clp-glow {
  position: absolute;
  z-index: 0;
  top: -40%;
  right: -15%;
  width: 360px;
  height: 360px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 65%);
  pointer-events: none;
}

.clp-inner {
  position: relative;
  z-index: 1;
}

.clp-eyebrow {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 14px;
}

/* the white club card forming */
.clp-card {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 18px 40px -20px rgba(26, 26, 26, 0.45);
}

.clp-crest {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.5px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  transition: transform 0.2s ease;

  &.is-empty {
    color: rgb(var(--v-theme-textSubtle));
    background: rgb(var(--v-theme-light));
  }
}

.clp-crest-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.clp-map {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgb(var(--v-theme-borderLight));

  img {
    display: block;
    width: 100%;
    height: 110px;
    object-fit: cover;
  }
}

.clp-name {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.3px;
  margin-top: 14px;
  line-height: 1.2;
  word-break: break-word;

  &.is-placeholder {
    color: rgb(var(--v-theme-textSubtle));
    font-weight: 600;
  }
}

.clp-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.clp-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-charcoal));

  &--ok {
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
  }
}

.clp-foot {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.clp-foot-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
  border-radius: 999px;
  padding: 2px 7px;
  margin-left: 6px;
}

.clp-perks {
  list-style: none;
  padding: 0;
  margin: 20px 0 0;

  li {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    padding: 6px 0;
  }
}

.clp-perk-ic {
  margin-right: 10px;
  color: #fff;
}
</style>
